import { useMemo, useState, type VoidFunctionComponent } from 'react';

import { window } from '../../../../../../../globals';
import { useCreateReportOption } from '../../../../../../../hooks/api/reports/useCreateReportOption';
import useReportOptions from '../../../../../../../hooks/api/reports/useReportOptions';
import SaveAsModal from '../../../../../../generic/SavedResources/modals/SaveAsModal';
import { type SavedResourceActionType } from '../../../../../../generic/SavedResources/SavedResourceDropdownActions';
import {
  SavedResourceDropdownContainer,
} from '../../../../../../generic/SavedResources/SavedResourceDropdownContainer';
import { type ResourceOption } from '../../../../../../generic/SavedResources/SavedResourceDropdownList';
import type { PartialReportConfig } from '../../../../../../../../shared/reports/types';

import { useSavedOptionsPermissions } from './useSavedOptionsPermissions';

type ActiveModal = 'saveAs' | 'rename' | 'delete' | null;

type Props = {
  currentConfig: PartialReportConfig;
  onApply: (config: PartialReportConfig | null) => void;
  isSavedOptionDirty: boolean;
};

export const SavedOptions: VoidFunctionComponent<Props> = ({ currentConfig, onApply, isSavedOptionDirty }) => {
  const { canCreate, canEdit, canDelete } = useSavedOptionsPermissions();

  const [selectedOptionId, setSelectedOptionId] = useState<number | null>(null);
  const [activeModal, setActiveModal] = useState<ActiveModal>(null);

  // We can only fetch options if the user is authenticated and has an organization ID.
  const organizationId = window.user?.organizationId || 0;
  const shouldFetch = organizationId > 0;
  const { data: options, isLoading } = useReportOptions(organizationId, shouldFetch);

  const createMutation = useCreateReportOption(organizationId);

  const selectOptions: ResourceOption[] = useMemo(
    () => [
      { id: null, name: 'None' },
      ...(options?.results.map((option) => ({ id: option.id, name: option.name })) || []),
    ],
    [options],
  );
  const currentOption = useMemo(
    () => selectOptions.find((option) => option.id === selectedOptionId) || { id: null, name: 'None' },
    [selectOptions, selectedOptionId],
  );

  const onChangeSelection = useMemo(
    () => (optionId: number | null) => {
      setSelectedOptionId(optionId);
      const selected = options?.results.find((o) => o.id === optionId);
      onApply(selected?.options ?? null);
    },
    [options, onApply],
  );

  const onSave = () => {
    // TODO: Implement save
  };

  const onSaveAs = (name: string) => {
    createMutation.mutate({ name, options: currentConfig }, { onSuccess: () => setActiveModal(null) });
  };

  const onRename = () => {
    // TODO: Implement rename
  };

  const onDelete = () => {
    // TODO: Implement delete
  };

  const isMutationLoading = createMutation.isPending;

  const actions = [
    {
      label: 'Reset',
      onSelect: () => {
        const selected = options?.results.find((o) => o.id === selectedOptionId);
        onApply(selected?.options ?? null);
      },
      isDisabled: !isSavedOptionDirty,
    },
    {
      label: 'Save',
      onSelect: onSave,
      isDisabled: !(canCreate && canEdit) || !isSavedOptionDirty || selectedOptionId === null,
    },
    {
      label: 'Save As...',
      onSelect: () => setActiveModal('saveAs'),
      isDisabled: !canCreate,
    },
    {
      label: 'Rename...',
      onSelect: onRename,
      isDisabled: !canEdit || selectedOptionId === null,
    },
    {
      label: 'Delete',
      onSelect: onDelete,
      isDisabled: !canDelete || selectedOptionId === null,
    },
  ];

  const onChange = (label: SavedResourceActionType) => actions.find((action) => action.label === label)?.onSelect();

  return (
    <>
      <SavedResourceDropdownContainer
        resourceType="option"
        resources={selectOptions}
        currentResource={currentOption}
        isLoading={isLoading}
        onChangeDropdownList={onChangeSelection}
        actions={actions}
        isDisabled={isMutationLoading}
        hasAnyEditPermission
        onChangeDropdownActions={onChange}
        hasNotificationDot={isSavedOptionDirty}
        shouldHideId
      />
      <SaveAsModal
        resourceType="option"
        isDisplayed={activeModal === 'saveAs'}
        isSaving={createMutation.isPending}
        onSave={onSaveAs}
        onHide={() => setActiveModal(null)}
      />
    </>
  );
};
